import { selectCurrentLayout } from '@src/components/AppLayout/AppLayoutSlice';
import { DEFAULT_LAYOUT_ID } from '@src/components/AppLayout/Layouts/tokens';
import { getTradingOrderDetailsRoute } from '@src/containers/Routes/routes';
import { useFeatureFlag } from '@src/hooks';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useAppStateSelector } from '@src/providers/AppStateProvider';
import type { CareOrder, FilterableProperty, UseFilterBuilderOutput } from '@talos/kyoko';
import {
  ACTION,
  agGridMenuSeparator,
  filterByCellValueMenuItem,
  IconName,
  Order,
  Quote,
  ROLE,
  useCallbackRef,
  useDisclosureWithContext,
  useMixpanel,
  type AgGridMenuItems,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, IRowNode } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { useOrderMenu } from '../Orders/useOrderMenu';
import { getCopyFundQuotePricesItem } from '../Quotes/MenuItems/copyFundQuotePricesItem';
import { useQuoteContextMenuActions } from '../Quotes/MenuItems/useQuoteContextMenuActions';
import { ModifyFixingDialog, type ModifyFixingProps } from './Dialogs/ModifyFixingDialog';
import { useCareOrderActionMenuItems } from './MenuItems/useCareOrderActionMenuItems';
import { useManualFillCareOrdersMenuItem } from './MenuItems/useManualFillCareOrdersMenuItem';
import { useModifyNettingMenuItem } from './MenuItems/useModifyNettingMenuItem';
import type { useNetCareOrdersMenuItem } from './MenuItems/useNetCareOrdersMenuItem';
import type { CareOrderBlotterEntity } from './types';
import { isCareOrderRow, isOrderRow, isQuoteRow, isQuoteRowWithOrder, LegRow, QuoteRow } from './types';
import { colIDToFilterBuilderKey } from './useCareOrderFilter';

interface UseCareOrderMenuParams {
  filterableProperties: FilterableProperty[];
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  onSendRFQ: (careOrder: CareOrder) => void;
  onSendOrder: (careOrder: CareOrder) => void;
  onManualFillCareOrders: (careOrders: CareOrder[]) => void;
  netCareOrdersMenu: ReturnType<typeof useNetCareOrdersMenuItem<CareOrder>>;
}

export function useCareOrderMenu({
  filterableProperties,
  openClause,
  onSendRFQ,
  onSendOrder,
  onManualFillCareOrders,
  netCareOrdersMenu,
}: UseCareOrderMenuParams): {
  getCustomContextMenuItems: (params: GetContextMenuItemsParams) => AgGridMenuItems;
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const { isRole } = useRoleAuth();
  const layoutId = useAppStateSelector(selectCurrentLayout)?.id ?? DEFAULT_LAYOUT_ID;
  const { getItem: getModifyFixingItem, dialogComponent: modifyFixingDialogComponent } =
    useModifyFixingContextMenuItem();
  const modifyNettingMenu = useModifyNettingMenuItem<CareOrder>();
  const manualFillCareOrdersMenu = useManualFillCareOrdersMenuItem({ onManualFillCareOrders });
  const careOrderActionMenuItems = useCareOrderActionMenuItems();
  const { getQuoteActionMenuItems, dialog: quoteActionDialog } = useQuoteContextMenuActions();

  const { enableCareOrderNetting, enableCareOrderManualFills, enableCareOrderOrders, careOrderBlockDirectExecution } =
    useFeatureFlag();

  const orderMenu = useOrderMenu({
    generateOrderDetailsRoute: getTradingOrderDetailsRoute,
    layoutId,
  });

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CareOrderBlotterEntity>) => {
    const entity = params.node?.data;
    const selectedNodes = params.api.getSelectedNodes();
    const selectedCareOrders = getSelectedCareOrderRowsFromNodes(selectedNodes);
    const netCareOrdersItem = enableCareOrderNetting
      ? netCareOrdersMenu.getMenuItem({
          selectedCareOrders,
        })
      : null;
    const modifyNettingItem = enableCareOrderNetting
      ? modifyNettingMenu.getMenuItem({
          contextCareOrder: entity && isCareOrderRow(entity) ? entity : undefined,
          selectedCareOrders,
        })
      : null;
    const manualFillCareOrdersItem =
      enableCareOrderManualFills && isRole(ROLE.TRADER)
        ? manualFillCareOrdersMenu.getMenuItem({
            selectedNodes,
          })
        : null;

    if (isGetContextMenuItemsParamsOrder(params)) {
      return orderMenu.getCustomContextMenuItems(params);
    }

    if (entity instanceof QuoteRow) {
      const quoteMenuItems: AgGridMenuItems = isGetContextMenuItemsParamsQuote(params)
        ? getQuoteActionMenuItems(entity)
        : [];

      const copyPricingItem = getCopyFundQuotePricesItem(getSelectedQuotesWithParentCOs(params));
      if (copyPricingItem) {
        quoteMenuItems.unshift(agGridMenuSeparator);
        quoteMenuItems.unshift(copyPricingItem);
      }

      const modifyFixingItem = getModifyFixingItem(params);
      if (modifyFixingItem) {
        quoteMenuItems.unshift(agGridMenuSeparator);
        quoteMenuItems.unshift(modifyFixingItem);
      }

      return quoteMenuItems;
    }

    if (entity instanceof LegRow) {
      return [];
    }

    if (!entity || !isCareOrderRow(entity)) {
      return [];
    }

    // Now we know that entity is a CareOrder
    let isCancelable: boolean = entity.isCancelable;
    if (params.node?.childrenAfterFilter != null) {
      for (const row of params.node.childrenAfterFilter) {
        if (isQuoteRow(row.data) && !row.data.isTerminal) {
          isCancelable = false;
          break;
        }
        if (isOrderRow(row.data) && !row.data.isComplete) {
          isCancelable = false;
          break;
        }
      }
    }

    const isTradable = entity.isTradable({
      careOrderBlockDirectExecution,
    });
    const filterItems = filterByCellValueMenuItem({
      params,
      filterableProperties,
      openClause,
      colIDToFilterBuilderKey,
      mixpanel,
    });
    const tradingItems = compact([
      {
        icon: `<i class="ag-icon ${IconName.BookOpen}"/>`,
        disabled: !isTradable.result,
        tooltip: isTradable.failureReason,
        name: 'Send RFQ',
        action: () => onSendRFQ(entity),
      },
      enableCareOrderOrders
        ? {
            icon: `<i class="ag-icon ${IconName.BookOpen}"/>`,
            disabled: !isTradable.result,
            tooltip: isTradable.failureReason,
            name: 'Send Order',
            action: () => onSendOrder(entity),
          }
        : null,
      manualFillCareOrdersItem,
    ]);
    const nettingItems = compact([
      netCareOrdersItem,
      modifyNettingItem,
      careOrderActionMenuItems.getViewNettingMenuItem(entity),
      careOrderActionMenuItems.getApproveWorkingOrderMenuItem(entity),
    ]);
    const approvalItems = compact([careOrderActionMenuItems.getApproveComplianceMenuItem(entity)]);
    const orderManagementItems = compact([
      careOrderActionMenuItems.getModifyMenuItem(entity),
      careOrderActionMenuItems.getCancelMenuItem({
        entity,
        isCancelable,
      }),
      careOrderActionMenuItems.getForceCancelMenuItem(entity),
    ]);
    const nettingMenuItems: AgGridMenuItems =
      nettingItems.length > 0 ? [agGridMenuSeparator, ...nettingItems, agGridMenuSeparator] : [];
    const items: AgGridMenuItems = [
      ...filterItems,
      ...(filterItems.length > 0 && tradingItems.length > 0 ? [agGridMenuSeparator] : []),
      ...tradingItems,
      ...nettingMenuItems,
      ...approvalItems,
      ...orderManagementItems,
    ];

    return compact(items);
  });

  const dialogs = useMemo(
    () => (
      <>
        {orderMenu.dialogComponents}
        {quoteActionDialog}
        {modifyFixingDialogComponent}
        {netCareOrdersMenu.dialogs}
        {modifyNettingMenu.dialogs}
        {careOrderActionMenuItems.dialogs}
      </>
    ),
    [
      orderMenu.dialogComponents,
      quoteActionDialog,
      modifyFixingDialogComponent,
      netCareOrdersMenu.dialogs,
      modifyNettingMenu.dialogs,
      careOrderActionMenuItems.dialogs,
    ]
  );

  return {
    getCustomContextMenuItems,
    dialogs,
  };
}

/** Pulls some strings to put together and prepare the ModifyFixingDialog and a getter to get the context menu item for you */
export const useModifyFixingContextMenuItem = () => {
  const { isAuthorized } = useRoleAuth();
  const { enableTakerModifyFixing, enableModifyFixingDialogV2 } = useFeatureFlag();
  const allowTakerModifyFixing = enableTakerModifyFixing && isAuthorized(ACTION.EDIT_ORDER_FIXING_DETAILS);
  const modifyFixingDisclosure = useDisclosureWithContext<ModifyFixingProps>();

  const getItem = useCallbackRef((params: GetContextMenuItemsParams<CareOrderBlotterEntity>) => {
    const entity = params.node?.data;
    if (!entity || !isQuoteRowWithOrder(entity)) {
      return undefined;
    }

    const showModifyFixingItem =
      parentIsDoneForDayCareOrder(params.node) &&
      allowTakerModifyFixing &&
      entity.PricingMode === 'SpreadToFixing' &&
      entity.OrdStatus === 'DoneForDay' &&
      entity.FixingDetails != null;
    if (showModifyFixingItem) {
      return {
        name: 'Modify Fixing',
        action: () => modifyFixingDisclosure.open({ quote: entity, orderID: entity.OrderID, mode: 'taker' }),
        icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
        disabled: entity.isMultitoken && !enableModifyFixingDialogV2,
      };
    }

    return undefined;
  });

  const dialogComponent = <ModifyFixingDialog {...modifyFixingDisclosure} />;

  return {
    getItem,
    dialogComponent,
  };
};

function isGetContextMenuItemsParamsOrder(
  params: GetContextMenuItemsParams
): params is GetContextMenuItemsParams<Order> {
  return params.node != null && params.node.data instanceof Order;
}

function isGetContextMenuItemsParamsQuote(
  params: GetContextMenuItemsParams
): params is GetContextMenuItemsParams<Quote> {
  return params.node != null && params.node.data instanceof Quote;
}

function parentIsDoneForDayCareOrder(node: IRowNode<any> | null) {
  const parent = node?.parent?.data;
  if (!isCareOrderRow(parent)) {
    return false;
  }

  return parent.OrdStatus === 'DoneForDay';
}

// Maps the params object from our blotter to the expected type of the StfQuotePrice menu item
function getSelectedQuotesWithParentCOs(
  params: GetContextMenuItemsParams<CareOrderBlotterEntity>
): Parameters<typeof getCopyFundQuotePricesItem>[0] {
  const selectedQuoteNodes = params.api
    .getSelectedNodes()
    .filter((node): node is IRowNode<CareOrderBlotterEntity> & { data: QuoteRow } => isQuoteRow(node.data));

  return selectedQuoteNodes.map(quoteNode => {
    const parentCO = isCareOrderRow(quoteNode.parent?.data) ? quoteNode.parent.data : undefined;
    return { quote: quoteNode.data, parentCO };
  });
}

export function getSelectedCareOrderRowsFromNodes(nodes: IRowNode<CareOrderBlotterEntity>[]): CareOrder[] {
  const isCareOrderNode = (
    node: IRowNode<CareOrderBlotterEntity>
  ): node is IRowNode<CareOrderBlotterEntity> & { data: CareOrder } => isCareOrderRow(node.data);

  return nodes.filter(isCareOrderNode).map(node => node.data);
}
